// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {BOOST_THIS_COMMUNITY_DESCRIPTOR} from '@app/features/boost/utils/GuildBoostMessages';
import {CANCEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {getFormattedShortDate} from '@app/features/user/utils/DateFormatting';
import type {UserBoostSlotResponse} from '@fluxer/schema/src/domains/boost/BoostResponseSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {useState} from 'react';

const CHOOSE_SLOT_DESCRIPTOR = msg({
	message: 'Choose a boost slot',
	comment:
		'Title of the dialog that asks which free boost slot boosts this community, when a bought one is among them.',
});
const PREMIUM_SLOT_DESCRIPTOR = msg({
	message: '{premiumProductName} slot',
	comment: 'A free boost slot that comes with Plus, in the boost slot choice.',
});
const PREMIUM_SLOT_LASTS_DESCRIPTOR = msg({
	message: 'Lasts as long as {premiumProductName}',
	comment: 'Under a Plus boost slot in the boost slot choice.',
});
const BOUGHT_SLOT_DESCRIPTOR = msg({
	message: 'Bought slot',
	comment: 'A free boost slot bought in the shop for 30 days, in the boost slot choice.',
});
const SLOT_UNTIL_DESCRIPTOR = msg({
	message: 'Until {date}',
	comment:
		'When a bought boost slot ends, under it in the boost slot choice. {date} is a short date such as Nov 12, 2026.',
});

// The free slots, the one ending first preselected. Premium slots are all alike, so one stands for them.
const GuildBoostSlotPicker = ({
	slots,
	onPick,
}: {
	slots: ReadonlyArray<UserBoostSlotResponse>;
	onPick: (slotId: string) => Promise<boolean>;
}) => {
	const {i18n} = useLingui();
	const [slotId, setSlotId] = useState(slots[0].id);
	const firstPremium = slots.find((slot) => slot.expires_at == null);
	const options = slots
		.filter((slot) => slot.expires_at != null || slot === firstPremium)
		.map((slot) => ({
			value: slot.id,
			name:
				slot.expires_at == null
					? i18n._(PREMIUM_SLOT_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})
					: i18n._(BOUGHT_SLOT_DESCRIPTOR),
			desc:
				slot.expires_at == null
					? i18n._(PREMIUM_SLOT_LASTS_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})
					: i18n._(SLOT_UNTIL_DESCRIPTOR, {date: getFormattedShortDate(slot.expires_at)}),
		}));
	return (
		<ConfirmModal
			title={i18n._(CHOOSE_SLOT_DESCRIPTOR)}
			description={
				<RadioGroup
					aria-label={i18n._(CHOOSE_SLOT_DESCRIPTOR)}
					options={options}
					value={slotId}
					onChange={setSlotId}
					data-flx="boost.slot-picker.radio-group"
				/>
			}
			primaryText={i18n._(BOOST_THIS_COMMUNITY_DESCRIPTOR)}
			secondaryText={i18n._(CANCEL_DESCRIPTOR)}
			onPrimary={async () => {
				await onPick(slotId);
			}}
			data-flx="boost.slot-picker.confirm-modal"
		/>
	);
};

export function openGuildBoostSlotPicker(
	slots: ReadonlyArray<UserBoostSlotResponse>,
	onPick: (slotId: string) => Promise<boolean>,
): void {
	ModalCommands.push(modal(() => <GuildBoostSlotPicker slots={slots} onPick={onPick} data-flx="boost.slot-picker" />));
}
